// =============================================================================
// Import limits config service — reads and writes import-config/limits.json.
//
// ── WHO WRITES, AND WHY IT IS NOT THE IMPORTER ───────────────────────────────
//
// The data-importer's App Runner instance role holds `ImportConfigReadOnly`:
// s3:GetObject on import-config/*, and NOTHING ELSE. That is the whole point of
// the design and it must stay that way — the service that ENFORCES the limits
// must not be able to RAISE them. If the importer could write this object, a
// compromise of the importer would be a compromise of every tenant's cap.
//
// So the System App writes through its own back-office API, with its own
// credentials, and the importer keeps read-only access. Do not "simplify" this
// by adding a PUT to the importer.
//
// ── WHO MAY WRITE ────────────────────────────────────────────────────────────
//
// Internal operators only. A tenant admin who can raise their own limit is not
// limited, so the backend must reject non-operator callers — the Cognito group
// check is the backend's job and this client does not attempt to enforce it.
// Read-only visibility for tenant admins is a separate, later question.
//
// ── CONCURRENCY ──────────────────────────────────────────────────────────────
//
// One JSON object, several possible editors. Every write carries If-Match with
// the ETag the config was read at, so a stale write is refused by S3 rather
// than silently clobbering someone.
//
// NOTE THE DELIBERATE DIFFERENCE FROM uiFeatureConfigService.ts: that one
// handles a 409 by reloading and RETRYING once, which means the second writer
// silently wins and the first writer's change disappears. That is wrong for
// limits — the numbers are a policy decision and losing one without anybody
// noticing is the failure worth designing against. Here a 409 is returned to
// the caller with the current server document attached, so the form can reload
// and show what changed before the operator decides.
// =============================================================================
import { getIdToken } from '../../../services/cognitoAuth';
import { IMPORTER_INTERNAL_SECRET } from '../../../hooks/useSignalR';
import { LimitsConfig, LimitsDocument } from '../models/importLimits';

const API_ROOT =
  (process.env.REACT_APP_IMPORT_CONFIG_API_URL as string | undefined) ??
  'http://localhost:5090/backoffice';

// Matches template.yaml's routes exactly. If these drift, the UI 404s.
const BASE = `${API_ROOT}/v1/import-limits`;

export class ImportLimitsError extends Error {
  constructor(message: string, public status?: number, public code?: string) {
    super(message);
    this.name = 'ImportLimitsError';
  }
}

/**
 * Someone else saved while this form was open. Carries THEIR version so the UI
 * can diff it against what the operator was editing.
 */
export class ImportLimitsConflict extends Error {
  constructor(public readonly current: LimitsDocument) {
    super('This configuration was changed by someone else while you were editing it.');
    this.name = 'ImportLimitsConflict';
  }
}

async function authHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  try {
    const token = await getIdToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  } catch { /* not signed in — the API answers 401 and the UI surfaces it */ }
  return headers;
}

async function parseError(res: Response): Promise<ImportLimitsError> {
  let body: any = null;
  try { body = await res.json(); } catch { /* non-JSON body */ }
  const code = body?.error?.code || body?.code;
  const message =
    body?.error?.message || body?.message || body?.detail ||
    (res.status === 401 || res.status === 403
      ? 'You are not signed in as an internal operator, so this configuration cannot be changed.'
      : `Request failed (${res.status})`);
  return new ImportLimitsError(message, res.status, code);
}

/** Read the live config plus the ETag needed to write it back. */
export async function loadLimits(): Promise<LimitsDocument> {
  const res = await fetch(BASE, { method: 'GET', headers: await authHeaders() });
  if (!res.ok) throw await parseError(res);
  const body = await res.json();
  // Accept the ETag from the body or the header, so the backend can supply
  // whichever is convenient without this breaking.
  const etag = body?.etag ?? res.headers.get('ETag') ?? '';
  return {
    config: (body?.config ?? body) as LimitsConfig,
    etag,
    lastModified: body?.lastModified ?? res.headers.get('Last-Modified'),
  };
}

/**
 * Write the config back, refusing to overwrite a newer version.
 *
 * Throws ImportLimitsConflict on 409 — never retries. See the header note.
 */
export async function saveLimits(config: LimitsConfig, etag: string): Promise<LimitsDocument> {
  const headers = await authHeaders();
  if (etag) { headers['If-Match'] = etag; }

  const res = await fetch(BASE, {
    method: 'PUT',
    headers,
    body: JSON.stringify({ config, etag }),
  });

  if (res.status === 409 || res.status === 412) {
    // 412 is what S3 itself returns for a failed If-Match; accept either so the
    // backend can pass it straight through.
    let current: LimitsDocument;
    try {
      const body = await res.json();
      current = {
        config: (body?.config ?? body?.current ?? {}) as LimitsConfig,
        etag: body?.etag ?? '',
        lastModified: body?.lastModified ?? null,
      };
    } catch {
      // The backend did not send the current document — fetch it ourselves so
      // the form can still show what it is up against.
      current = await loadLimits();
    }
    throw new ImportLimitsConflict(current);
  }

  if (!res.ok) throw await parseError(res);

  const body = await res.json().catch(() => null);
  return {
    config: (body?.config ?? config) as LimitsConfig,
    etag: body?.etag ?? res.headers.get('ETag') ?? '',
    lastModified: body?.lastModified ?? null,
  };
}

/**
 * A flat list of the differences between two configs, for the conflict panel.
 * Deliberately dumb and readable: a full JSON diff would be noise here — an
 * operator needs "who changed which number to what", not a structural diff.
 */
export function describeDifferences(mine: LimitsConfig, theirs: LimitsConfig): string[] {
  const out: string[] = [];
  const flat = (cfg: LimitsConfig): Record<string, any> => {
    const acc: Record<string, any> = {};
    const walk = (o: any, path: string) => {
      if (o === null || typeof o !== 'object' || Array.isArray(o)) { acc[path] = o; return; }
      Object.keys(o).forEach(k => {
        if (k === '_readme' || k === '_comment') return;   // prose, not policy
        walk(o[k], path ? `${path}.${k}` : k);
      });
    };
    walk(cfg, '');
    return acc;
  };
  const a = flat(mine), b = flat(theirs);
  const keys = Array.from(new Set([...Object.keys(a), ...Object.keys(b)])).sort();
  for (const k of keys) {
    if (JSON.stringify(a[k]) === JSON.stringify(b[k])) continue;
    const from = a[k] === undefined ? '(unset)' : String(a[k]);
    const to = b[k] === undefined ? '(removed)' : String(b[k]);
    out.push(`${k}: yours ${from} → theirs ${to}`);
  }
  return out;
}

/**
 * What the importer DETECTS for a tenant — isPurchase from the tenant record,
 * read server-side and cached there for 15 minutes.
 *
 * The UI asks rather than deriving it. Only the server can read the tenant
 * record, and having the admin screen guess at a commercial flag would be
 * exactly the kind of second source of truth this feature exists to remove.
 *
 * Served by GET /import/meta on the data-importer as `tier_detail`. That
 * endpoint resolves the tenant from the caller's verified claims, so the guid
 * is passed explicitly here for the operator case, where the caller is staff
 * looking at someone else's tenant.
 */
export async function detectTier(
  tenantGuid: string,
): Promise<{ tier?: string; detected?: boolean; pinned?: boolean; detectedTier?: string }> {
  // NOTE: served by the IMPORTER (GET /import/meta -> tier_detail), not by the
  // limits API — only the importer can read the tenant record. Separate base
  // URL on purpose; do not fold it into BASE.
  const importerBase = (process.env.REACT_APP_IMPORTER_API_URL as string | undefined) ?? '';
  // Added here rather than in authHeaders(): that helper also serves the limits
  // API, which has no business seeing the importer's secret.
  const res = await fetch(`${importerBase}/import/meta?tenantGuid=${encodeURIComponent(tenantGuid)}`, {
    method: 'GET',
    headers: { ...(await authHeaders()), 'X-Internal-Secret': IMPORTER_INTERNAL_SECRET },
  });
  if (!res.ok) throw await parseError(res);
  const body = await res.json();
  // Accept the importer's tier_detail shape straight through, or a flattened
  // one, so the back-office API can proxy either without this caring.
  const d = body?.tier_detail ?? body;
  return {
    tier: d?.detected_tier ?? d?.tier,
    detected: d?.detected ?? false,
    pinned: d?.pinned ?? false,
    detectedTier: d?.detected_tier,
  };
}
